import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ImagePlus } from 'lucide-react'
import { toast } from 'sonner'

import type { ShopGuideVideo } from '@/lib/api/shop-guide-videos-api'
import type { ShopMedia } from '@/lib/api/shop-media-api'
import type { ShopGuideVideoForm } from '@/lib/schemas/shop-guide-video-schemas'
import { MediaPicker } from '@/components/media/media-picker'
import {
    ErrorText,
    SwitchField,
    TextField,
} from '@/components/retail/form-bits'
import {
    formatGuideDuration,
    sameGroupName,
} from '@/components/retail/guide-videos/guide-video-utils'
import { setShopServerErrors } from '@/components/retail/shop-errors'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useSaveShopGuideVideo } from '@/hooks/use-shop-guide-videos-query'
import { thumbOf } from '@/lib/api/shop-catalog-api'
import { m } from '@/lib/paraglide/messages'
import { createShopGuideVideoSchema } from '@/lib/schemas/shop-guide-video-schemas'

const EMPTY: ShopGuideVideoForm = {
    title: '',
    description: '',
    group_name: '',
    video_media_id: '',
    video_url: '',
    video_name: '',
    poster_media_id: null,
    poster_url: null,
    duration_seconds: null,
    active: true,
}

function toForm(v: ShopGuideVideo): ShopGuideVideoForm {
    return {
        title: v.title,
        description: v.description,
        group_name: v.group_name,
        video_media_id: v.video_media_id,
        video_url: v.video_url,
        video_name: v.video_url.split('/').pop() ?? '',
        poster_media_id: v.poster_media_id,
        poster_url: v.poster_url,
        duration_seconds: v.duration_seconds,
        active: v.active,
    }
}

interface Props {
    /** null = đóng; 'new' = thêm mới. */
    editing: ShopGuideVideo | 'new' | null
    /** Nhóm đang có — gợi ý cho ô Nhóm. */
    groups: string[]
    /** Số video mỗi nhóm — để báo đổi cách viết nhóm áp cho cả nhóm. */
    groupCounts?: Record<string, number>
    onClose: () => void
}

/**
 * Hộp thêm / sửa video hướng dẫn. Ô Video / Ảnh bìa mở thư viện ảnh
 * (MediaPicker: tìm có debounce, cuộn tải thêm, tải file mới ngay trong hộp).
 * Thời lượng đọc từ metadata của video xem trước (preload=metadata).
 */
export function GuideVideoFormDialog({
    editing,
    groups,
    groupCounts = {},
    onClose,
}: Props) {
    return (
        <Dialog open={editing !== null} onOpenChange={(o) => !o && onClose()}>
            {editing !== null && (
                <DialogContent className="flex max-h-[92svh] flex-col sm:max-w-2xl">
                    <FormBody
                        key={editing === 'new' ? 'new' : editing.id}
                        editing={editing}
                        groups={groups}
                        groupCounts={groupCounts}
                        onClose={onClose}
                    />
                </DialogContent>
            )}
        </Dialog>
    )
}

function FormBody({
    editing,
    groups,
    groupCounts,
    onClose,
}: {
    editing: ShopGuideVideo | 'new'
    groups: string[]
    groupCounts: Record<string, number>
    onClose: () => void
}) {
    const save = useSaveShopGuideVideo()
    const [picker, setPicker] = useState<'video' | 'poster' | null>(null)
    const form = useForm<ShopGuideVideoForm>({
        resolver: zodResolver(createShopGuideVideoSchema()),
        defaultValues: editing === 'new' ? EMPTY : toForm(editing),
    })
    const { setValue, watch } = form
    const videoURL = watch('video_url')
    const posterURL = watch('poster_url')
    const duration = watch('duration_seconds')

    const pick = ([item]: ShopMedia[]) => {
        if (!item) return
        const opts = { shouldDirty: true, shouldValidate: true }
        if (picker === 'video') {
            setValue('video_media_id', item.id, opts)
            setValue('video_url', item.url)
            setValue('video_name', item.filename)
            setValue('duration_seconds', null)
        } else {
            setValue('poster_media_id', item.id, opts)
            setValue('poster_url', item.url)
        }
    }

    const submit = form.handleSubmit(async (v) => {
        try {
            await save.mutateAsync({
                id: editing === 'new' ? undefined : editing.id,
                input: {
                    title: v.title,
                    description: v.description.trim(),
                    group_name: v.group_name,
                    video_media_id: v.video_media_id,
                    poster_media_id: v.poster_media_id,
                    duration_seconds: v.duration_seconds,
                    active: v.active,
                },
            })
            toast.success(m.guideVideo_saved())
            onClose()
        } catch (err) {
            setShopServerErrors(form.setError, err, m.guideVideo_save_failed())
        }
    })

    return (
        <FormProvider {...form}>
            <form
                noValidate
                onSubmit={submit}
                className="flex min-h-0 flex-1 flex-col gap-4"
            >
                <DialogHeader>
                    <DialogTitle>
                        {editing === 'new'
                            ? m.guideVideo_form_add_title()
                            : m.guideVideo_form_edit_title()}
                    </DialogTitle>
                    <DialogDescription>
                        {m.guideVideo_form_hint()}
                    </DialogDescription>
                </DialogHeader>
                <div className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1">
                    <Field
                        name="video_media_id"
                        label={m.guideVideo_field_video()}
                        required
                    >
                        {videoURL && (
                            <video
                                data-testid="guide-video-preview"
                                src={videoURL}
                                controls
                                playsInline
                                preload="metadata"
                                className="max-h-56 w-full rounded-md bg-black object-contain"
                                onLoadedMetadata={(e) => {
                                    const d = e.currentTarget.duration
                                    if (Number.isFinite(d) && d >= 0)
                                        setValue(
                                            'duration_seconds',
                                            Math.round(d),
                                        )
                                }}
                            />
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                id="video_media_id"
                                type="button"
                                size="sm"
                                variant="outline"
                                aria-label={m.media_choose_aria({
                                    action: videoURL
                                        ? m.guideVideo_replace_video()
                                        : m.guideVideo_choose_video(),
                                    label: m.guideVideo_field_video(),
                                })}
                                onClick={() => setPicker('video')}
                            >
                                {videoURL
                                    ? m.guideVideo_replace_video()
                                    : m.guideVideo_choose_video()}
                            </Button>
                            <span className="min-w-0 truncate text-xs text-muted-foreground">
                                {watch('video_name')}
                                {duration != null &&
                                    ` · ${m.guideVideo_duration_read({ duration: formatGuideDuration(duration) })}`}
                            </span>
                        </div>
                    </Field>
                    <TextField
                        name="title"
                        label={m.guideVideo_field_title()}
                    />
                    <Field
                        name="group_name"
                        label={m.guideVideo_field_group()}
                        required
                    >
                        <Input
                            id="group_name"
                            list="guide-video-groups"
                            autoComplete="off"
                            aria-invalid={!!form.formState.errors.group_name}
                            // Trình đọc màn hình đọc kèm gợi ý + ghi chú đổi cách viết / gộp nhóm.
                            aria-describedby="guide-video-group-hint guide-video-group-notes"
                            {...form.register('group_name')}
                        />
                        <datalist id="guide-video-groups">
                            {groups.map((g) => (
                                <option key={g} value={g} />
                            ))}
                        </datalist>
                        <p
                            id="guide-video-group-hint"
                            className="text-xs text-muted-foreground"
                        >
                            {m.guideVideo_field_group_hint()}
                        </p>
                        <div id="guide-video-group-notes">
                            <GroupRenameNote
                                editing={editing}
                                groupCounts={groupCounts}
                                value={watch('group_name')}
                            />
                            <GroupMergeNote
                                editing={editing}
                                groups={groups}
                                value={watch('group_name')}
                            />
                        </div>
                    </Field>
                    <TextField
                        name="description"
                        label={m.guideVideo_field_description()}
                        rows={3}
                    />
                    <Field
                        name="poster_media_id"
                        label={m.guideVideo_field_poster()}
                    >
                        <div className="flex flex-wrap items-end gap-3">
                            <div className="flex aspect-video w-40 items-center justify-center overflow-hidden rounded-md border bg-muted">
                                {posterURL ? (
                                    <img
                                        src={thumbOf(posterURL)}
                                        alt={m.guideVideo_field_poster()}
                                        className="size-full object-contain"
                                    />
                                ) : (
                                    <ImagePlus
                                        aria-hidden="true"
                                        className="size-6 text-muted-foreground"
                                    />
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    id="poster_media_id"
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    aria-label={m.media_choose_aria({
                                        action: posterURL
                                            ? m.guideVideo_replace_poster()
                                            : m.guideVideo_choose_poster(),
                                        label: m.guideVideo_field_poster(),
                                    })}
                                    onClick={() => setPicker('poster')}
                                >
                                    {posterURL
                                        ? m.guideVideo_replace_poster()
                                        : m.guideVideo_choose_poster()}
                                </Button>
                                {posterURL && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        className="text-destructive"
                                        onClick={() => {
                                            setValue('poster_media_id', null, {
                                                shouldDirty: true,
                                            })
                                            setValue('poster_url', null)
                                        }}
                                    >
                                        {m.guideVideo_remove_poster()}
                                    </Button>
                                )}
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {m.guideVideo_field_poster_hint()}
                        </p>
                    </Field>
                    <SwitchField
                        name="active"
                        label={m.guideVideo_field_active()}
                    />
                    <ErrorText
                        text={form.formState.errors.root?.server?.message}
                    />
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose}>
                        {m.common_cancel()}
                    </Button>
                    <Button type="submit" disabled={save.isPending}>
                        {m.common_save()}
                    </Button>
                </DialogFooter>
            </form>
            <MediaPicker
                open={picker !== null}
                onOpenChange={(o) => !o && setPicker(null)}
                kind={picker === 'poster' ? 'image' : 'video'}
                onSelect={pick}
                title={
                    picker === 'poster'
                        ? m.guideVideo_field_poster()
                        : m.guideVideo_field_video()
                }
            />
        </FormProvider>
    )
}

/**
 * Sửa video của nhóm ≥2 video mà tên nhóm mới chỉ khác cách viết (dấu, hoa
 * thường, khoảng trắng): server đổi tên cả nhóm — báo trước để không bất ngờ.
 */
function GroupRenameNote({
    editing,
    groupCounts,
    value,
}: {
    editing: ShopGuideVideo | 'new'
    groupCounts: Record<string, number>
    value: string
}) {
    if (editing === 'new') return null
    const current = editing.group_name
    const count = groupCounts[current] ?? 0
    const next = value.trim().split(/\s+/).join(' ')
    if (count < 2 || next === current || !sameGroupName(next, current))
        return null
    return (
        <p className="text-xs text-amber-700 dark:text-amber-400" role="status">
            {m.guideVideo_group_rename_note({ count })}
        </p>
    )
}

/**
 * Tên nhóm chỉ khác cách viết so với MỘT NHÓM KHÁC đang có: server gộp vào
 * nhóm đó (reuseGroupName) — báo trước. Nhóm hiện tại của chính video không
 * tính (đó là sửa chính tả, xem GroupRenameNote); trùng chính xác thì thôi.
 */
function GroupMergeNote({
    editing,
    groups,
    value,
}: {
    editing: ShopGuideVideo | 'new'
    groups: string[]
    value: string
}) {
    const next = value.trim().split(/\s+/).join(' ')
    const current = editing === 'new' ? null : editing.group_name
    if (!next || (current !== null && sameGroupName(next, current))) return null
    const target = groups.find(
        (g) => g !== next && g !== current && sameGroupName(g, next),
    )
    if (!target) return null
    return (
        <p className="text-xs text-amber-700 dark:text-amber-400" role="status">
            {m.guideVideo_group_merge_note({ name: target })}
        </p>
    )
}
